import { useState, useEffect, useCallback } from 'react'
import { decodeShareUrl } from './useShareUrl'

const STORAGE_KEY = 'mortgage-calculator-state'

interface PersistedState {
  propertyPrice: number
  downPaymentPercent: number
  loanYears: number
  wiborRate: number
  bankMargin: number
  monthlyIncome: number
  monthlyOverpayment: number
  monthlyRent: number
  bankCommissionPercent: number
  propertyInsuranceYearly: number
  isSecondaryMarket: boolean
  installmentType: 'equal' | 'decreasing'
}

function loadState(): Partial<PersistedState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function usePersistedState(defaults: PersistedState) {
  // URL params take priority over localStorage (for shared links)
  const urlState = decodeShareUrl()
  const saved = { ...loadState(), ...urlState }

  // Clean URL params after reading them (keep hash for section scrolling)
  if (urlState && window.location.search) {
    const hash = window.location.hash
    window.history.replaceState({}, '', window.location.pathname + hash)
  }

  const [propertyPrice, setPropertyPrice] = useState(saved.propertyPrice ?? defaults.propertyPrice)
  const [downPaymentPercent, setDownPaymentPercent] = useState(saved.downPaymentPercent ?? defaults.downPaymentPercent)
  const [loanYears, setLoanYears] = useState(saved.loanYears ?? defaults.loanYears)
  const [wiborRate, setWiborRate] = useState(saved.wiborRate ?? defaults.wiborRate)
  const [bankMargin, setBankMargin] = useState(saved.bankMargin ?? defaults.bankMargin)
  const [monthlyIncome, setMonthlyIncome] = useState(saved.monthlyIncome ?? defaults.monthlyIncome)
  const [monthlyOverpayment, setMonthlyOverpayment] = useState(saved.monthlyOverpayment ?? defaults.monthlyOverpayment)
  const [monthlyRent, setMonthlyRent] = useState(saved.monthlyRent ?? defaults.monthlyRent)
  const [bankCommissionPercent, setBankCommissionPercent] = useState(saved.bankCommissionPercent ?? defaults.bankCommissionPercent)
  const [propertyInsuranceYearly, setPropertyInsuranceYearly] = useState(saved.propertyInsuranceYearly ?? defaults.propertyInsuranceYearly)
  const [isSecondaryMarket, setIsSecondaryMarket] = useState(saved.isSecondaryMarket ?? defaults.isSecondaryMarket)
  const [installmentType, setInstallmentType] = useState<'equal' | 'decreasing'>(saved.installmentType ?? defaults.installmentType)

  // Auto-save on any change
  useEffect(() => {
    saveState({
      propertyPrice,
      downPaymentPercent,
      loanYears,
      wiborRate,
      bankMargin,
      monthlyIncome,
      monthlyOverpayment,
      monthlyRent,
      bankCommissionPercent,
      propertyInsuranceYearly,
      isSecondaryMarket,
      installmentType,
    })
  }, [propertyPrice, downPaymentPercent, loanYears, wiborRate, bankMargin, monthlyIncome, monthlyOverpayment, monthlyRent, bankCommissionPercent, propertyInsuranceYearly, isSecondaryMarket, installmentType])

  const reset = useCallback(() => {
    setPropertyPrice(defaults.propertyPrice)
    setDownPaymentPercent(defaults.downPaymentPercent)
    setLoanYears(defaults.loanYears)
    setWiborRate(defaults.wiborRate)
    setBankMargin(defaults.bankMargin)
    setMonthlyIncome(defaults.monthlyIncome)
    setMonthlyOverpayment(defaults.monthlyOverpayment)
    setMonthlyRent(defaults.monthlyRent)
    setBankCommissionPercent(defaults.bankCommissionPercent)
    setPropertyInsuranceYearly(defaults.propertyInsuranceYearly)
    setIsSecondaryMarket(defaults.isSecondaryMarket)
    setInstallmentType(defaults.installmentType)
    localStorage.removeItem(STORAGE_KEY)
  }, [defaults])

  return {
    propertyPrice, setPropertyPrice,
    downPaymentPercent, setDownPaymentPercent,
    loanYears, setLoanYears,
    wiborRate, setWiborRate,
    bankMargin, setBankMargin,
    monthlyIncome, setMonthlyIncome,
    monthlyOverpayment, setMonthlyOverpayment,
    monthlyRent, setMonthlyRent,
    bankCommissionPercent, setBankCommissionPercent,
    propertyInsuranceYearly, setPropertyInsuranceYearly,
    isSecondaryMarket, setIsSecondaryMarket,
    installmentType, setInstallmentType,
    reset,
  }
}
